import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { Wrapper } from '@/components/template/PartnerTemplate/Wrapper';
import {
  BoltIcon,
  ChartBarIcon,
  GlobeAmericasIcon,
} from '@heroicons/react/24/solid';
import Image from 'next/image';
import { FC } from 'react';

const DesktopImage = () => {
  return (
    <div className="relative flex aspect-square items-center justify-center">
      <div className="absolute h-full w-full">
        <Image
          src={'/images/partner/hero-img-desktop.png'}
          alt={'EV Tools Background'}
          layout="fill"
          objectFit="contain"
          priority
        />
      </div>

      <div className="absolute h-full w-full rounded-full backdrop-blur-partnerHeroOuter" />
      <div className="absolute h-[60%] w-[60%] rounded-full backdrop-blur-partnerHeroInner" />

      <div className="flex h-[85%] w-[85%] items-center justify-center rounded-full backdrop-blur-partnerHeroMid">
        <div className="flex h-full w-full animate-glow bg-[#33ffcc] blur-partnerHero" />
      </div>

      <div className="absolute h-[90%] w-[90%] translate-y-[25%]">
        <Image
          src={'/images/partner/White-Ford-Mach-E-CUTOUT.png'}
          alt={'EV Tools Background'}
          layout="fill"
          objectFit="contain"
          priority
        />
      </div>
    </div>
  );
};

type FeatureCardVariant = 'tools' | 'strategies' | 'visibility';

const FeatureCard = ({ variant }: { variant: FeatureCardVariant }) => {
  let icon = <BoltIcon className="h-xl w-xl text-white" />;
  let title = 'Purpose-Built tools for EV success';
  let copy =
    'Exclusive solutions tailored specifically for electric vehicles to meet the demands of EV shoppers and drive meaningful growth.';
  if (variant === 'strategies') {
    icon = <ChartBarIcon className="h-xl w-xl text-white" />;
    title = 'Proven strategies, Measurable results';
    copy =
      'Increased engagement and relevant leads drive higher sales utilizing professional insights and data-driven strategies.';
  }
  if (variant === 'visibility') {
    icon = <GlobeAmericasIcon className="h-xl w-xl text-white" />;
    title = 'Maximize visibility';
    copy =
      'Expand your market with a seamless online presence that connects your inventory to a nationwide EV audience.';
  }

  return (
    <div className="flex flex-grow flex-col gap-xl rounded-medium border border-neutral-200 bg-neutral-0 p-xl">
      <div className="flex w-full">
        <div className="flex rounded-full bg-blue-medium p-[20px]">{icon}</div>
      </div>
      <div className="flex w-full flex-col gap-m">
        <h4 className="flex text-h4SemiBold text-brandMidnight">{title}</h4>
        <div className="flex text-body1Regular text-neutral-800">{copy}</div>
      </div>
    </div>
  );
};

const MobileImage = () => {
  return (
    <div className="relative flex aspect-square items-center justify-center">
      <div className="absolute h-full w-full">
        <Image
          src={'/images/partner/hero-img-mobile.png'}
          alt={'EV Tools Background'}
          layout="fill"
          objectFit="contain"
          priority
        />
      </div>

      <div className="flex h-[90%] w-[90%] items-center justify-center backdrop-blur-partnerHero">
        <div className="flex h-full w-full animate-glow rounded-full bg-[#33ffcc] blur-partnerHero" />
      </div>

      <div className="absolute h-[80%] w-[80%] translate-y-[30%]">
        <Image
          src={'/images/partner/White-Ford-Mach-E-CUTOUT.png'}
          alt={'EV Tools Background'}
          layout="fill"
          objectFit="contain"
          priority
        />
      </div>
    </div>
  );
};
export const PartnerHero: FC = () => {
  const variants: FeatureCardVariant[] = ['tools', 'strategies', 'visibility'];
  return (
    <div>
      <section className="flex flex-col gap-[120px] rounded-b-[40px] bg-brandMidnight py-3xl ml:gap-m ml:pt-0">
        <Wrapper variant="left">
          <section className="flex w-full flex-col items-center justify-between gap-xl ml:flex-row">
            <section className="relative z-10 flex w-full flex-col items-start justify-center gap-3xl ml:w-[50%] l:w-[30%]">
              <h1 className="text-homepageH1SemiBold text-neutral-0">
                Boost your sales with Advanced EV Marketplace Tools
              </h1>
              <div className="hidden ml:flex">
                <Button
                  variant={ButtonVariants.Tertiary}
                  aria-label="Become a Partner"
                >
                  Become a Partner
                </Button>
              </div>
            </section>

            <section className="flex w-full items-center ml:w-[50%] ml:pr-m l:items-start">
              <div className="block w-full l:hidden">
                <MobileImage />
              </div>
              <div className="hidden w-full l:block">
                <DesktopImage />
              </div>
            </section>

            <section className="flex w-full ml:hidden">
              <Button
                variant={ButtonVariants.Tertiary}
                aria-label="Become a Partner"
              >
                Become a Partner
              </Button>
            </section>
          </section>
        </Wrapper>
        <Wrapper>
          <section className="flex w-full flex-col gap-xl ml:flex-row">
            {variants.map((variant) => (
              <FeatureCard key={variant} variant={variant} />
            ))}
          </section>
        </Wrapper>
      </section>
    </div>
  );
};
